/**
 * Billing Service
 *
 * Handles billing-related API calls including:
 * - Current billing information
 * - Plan upgrades via Stripe Checkout
 * - Stripe Customer Portal
 * - Subscription management
 * - Invoice history
 */

import api from './api';

// Types matching backend BillingInfo
export interface BillingInfo {
  plan: {
    id: number;
    name: string;
    description: string | null;
  };
  status: 'active' | 'trialing' | 'past_due' | 'canceled';
  billingCycle: 'monthly' | 'yearly';
  seats: {
    current: number; // Billed seats
    used: number;    // Actual organization members
  };
  pricing: {
    perSeat: number;
    total: number;
    currency: string;
  };
  nextBilling: {
    date: string | null;
    amount: number | null;
  };
  gracePeriod: {
    isInGracePeriod: boolean;
    startDate: string | null;
    endDate: string | null;
    daysRemaining: number | null;
  };
  stripe: {
    customerId: string | null;
    subscriptionId: string | null;
  };
  cancelAtPeriodEnd: boolean;
  // Pending downgrade - scheduled to take effect at next billing cycle
  pendingDowngrade: {
    planId: number;
    planName: string;
    billingCycle: 'monthly' | 'yearly';
    effectiveDate: string;
  } | null;
  // Active trial — when set, `plan` above is the TRIAL plan being used
  trial: {
    endDate: string;
    daysRemaining: number;
    // Plan the org falls back to when the trial ends — the plans grid treats
    // this as "current" so the trialed plan stays purchasable
    revertsToPlanId: number;
    revertsToPlanName: string;
  } | null;
}

export interface AvailablePlan {
  id: number;
  name: string;
  description: string | null;
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string; // 'USD' or 'JPY'
  features: string[];
  limits: Record<string, number | null>;
  isCurrentPlan: boolean;
  isContactSales: boolean;
  taxIncluded: boolean; // Whether prices include tax
  taxRate: number; // Tax rate percentage (e.g., 10 for 10%)
  /**
   * SailLab's commission on payments the host collects from invitees, in basis
   * points (300 = 3%, 0 = we take nothing). Already resolved server-side against
   * the global kill switch, so render it as-is.
   *
   * This is SailLab's cut ONLY — Stripe's processing fee (~3.6% on Connect)
   * applies on every plan including the 0% ones, so any UI quoting this number
   * must say "+ Stripe fees".
   */
  paymentCommissionBps: number;
}

/**
 * Billing history entry type
 * Can be an invoice, refund, or failed payment
 */
export type BillingEntryType = 'invoice' | 'refund' | 'failed_payment';

/**
 * Billing history entry status
 * Combines invoice statuses and payment statuses
 */
export type BillingEntryStatus =
  | 'draft' | 'open' | 'paid' | 'void' | 'uncollectible'  // Invoice statuses
  | 'refunded' | 'failed';  // Payment statuses

/**
 * Unified billing history entry
 * Represents invoices, refunds, and failed payments in a single format
 */
export interface BillingHistoryEntry {
  id: number;
  type: BillingEntryType;
  invoiceNumber: string | null;
  description: string;
  amount: number;
  currency: string;
  status: BillingEntryStatus;
  periodStart: string | null;
  periodEnd: string | null;
  paidAt: string | null;
  pdfUrl: string | null;
  createdAt: string;
  // Additional fields for failed payments
  failureReason?: string;
  failedAt?: string | null;
}

export interface SeatChangePreview {
  proratedAmount: number;
  newTotal: number;
}

export interface CanAddMemberResult {
  canAdd: boolean;
  reason?: string;
}

export interface PaymentMethod {
  id: string;
  brand: string; // visa, mastercard, amex, etc.
  last4: string;
  expMonth: number;
  expYear: number;
}

export interface SetupIntentResponse {
  clientSecret: string;
}

export type Currency = 'USD' | 'JPY';
export type BillingRegion = 'JP' | 'DEFAULT';

/**
 * Get currency for a billing region
 * Japan (JP) uses JPY, all other regions use USD
 */
export const getCurrencyForRegion = (region: BillingRegion): Currency => {
  return region === 'JP' ? 'JPY' : 'USD';
};

/**
 * Get default billing region based on browser locale (for initial selection)
 * This is just a suggestion - user can change it on the billing page
 */
export const getDefaultBillingRegion = (): BillingRegion => {
  const locale = navigator.language || navigator.languages?.[0] || 'en-US';
  // Suggest Japan region for Japanese locale users
  if (locale.startsWith('ja') || locale === 'ja-JP') {
    return 'JP';
  }
  return 'DEFAULT';
};

/**
 * Response type for the combined billing page data endpoint
 */
export interface BillingPageData {
  billingInfo: BillingInfo;
  invoiceHistory: BillingHistoryEntry[];
  paymentMethod: PaymentMethod | null;
}

/**
 * Get all billing page data in a single request (billing info + history + payment method).
 *
 * This replaces 3 separate API calls (getCurrentBillingInfo + getInvoiceHistory + getPaymentMethod)
 * with 1 call. The backend runs all Stripe API calls in parallel, reducing total latency from
 * ~2000ms to ~500-800ms.
 */
export const getBillingPageData = async (): Promise<BillingPageData> => {
  return api.get('/billing/page-data');
};

/**
 * Fetch the Stripe publishable key from the server.
 *
 * Single source of truth: server's STRIPE_PUBLISHABLE_KEY env var. Replaces
 * the old REACT_APP_STRIPE_PUBLISHABLE_KEY build-time approach so the key
 * only needs to be set in one place and rotations don't require a client
 * rebuild.
 */
export const getStripeConfig = async (): Promise<{ publishableKey: string }> => {
  return api.get('/billing/config');
};

/**
 * Get current billing information for the user's organization
 */
export const getCurrentBillingInfo = async (): Promise<BillingInfo> => {
  return api.get('/billing/current');
};

/**
 * Get available plans for upgrade
 * @param region - Billing region ('JP' for Japan, 'DEFAULT' for others)
 */
export const getAvailablePlans = async (region: BillingRegion = 'DEFAULT'): Promise<AvailablePlan[]> => {
  return api.get(`/billing/plans?region=${region}`);
};

/**
 * Result of requesting a plan upgrade.
 *
 * A customer with no subscription yet gets `checkoutUrl` and must be redirected
 * to Stripe. A customer who already has one has their plan changed and charged
 * server-side before this resolves — there is nowhere to redirect them, so they
 * get `changed: true` with the new state instead.
 */
export interface PlanUpgradeResult {
  checkoutUrl?: string;
  changed?: boolean;
  billingInfo?: BillingInfo;
}

/**
 * Request a plan upgrade
 *
 * Currency and tax are determined server-side based on user's profile country:
 * - Japan (JP) = JPY currency + 10% consumption tax (消費税)
 * - All others = USD currency, no tax
 *
 * @param planId - The plan ID to upgrade to
 * @param billingCycle - 'monthly' or 'yearly'
 * @param _region - Deprecated, kept for API compatibility. Region is now determined server-side.
 */
export const createCheckoutSession = async (
  planId: number,
  billingCycle: 'monthly' | 'yearly',
  _region: BillingRegion = 'DEFAULT'
): Promise<PlanUpgradeResult> => {
  // Currency and tax are now determined server-side based on user's profile country
  // No need to send currency from frontend
  return api.post('/billing/checkout-session', { planId, billingCycle });
};

/**
 * Verify checkout session after returning from Stripe
 * This is the synchronous verification that complements webhooks
 *
 * Called when user returns from checkout with session_id in URL
 * - Fetches session from Stripe
 * - Verifies payment was successful
 * - Creates/updates subscription in database
 * - Returns updated billing info
 *
 * IDEMPOTENT: Safe to call multiple times - won't create duplicate subscriptions
 *
 * @param sessionId - Stripe Checkout Session ID from URL params
 */
export const verifyCheckoutSession = async (sessionId: string): Promise<BillingInfo> => {
  return api.post('/billing/verify-session', { sessionId });
};

/**
 * Create Stripe Customer Portal session
 * Returns the portal URL to redirect to
 */
export const createPortalSession = async (): Promise<{ portalUrl: string }> => {
  return api.post('/billing/portal-session', {});
};

/**
 * Preview seat count change (shows proration amount)
 */
export const previewSeatChange = async (
  newSeatCount: number
): Promise<SeatChangePreview> => {
  return api.post('/billing/preview-seats', { newSeatCount });
};

/**
 * Update seat count (triggers prorated charge)
 */
export const updateSeatCount = async (
  newSeatCount: number
): Promise<BillingInfo> => {
  return api.post('/billing/update-seats', { newSeatCount });
};

/**
 * Result of cancelling.
 *
 * `period_end` is the normal case: a paid Stripe subscription runs to the end of
 * the period already paid for. `immediately` is a plan that was granted rather
 * than bought — there is no paid period to run out, so it ends on confirmation
 * and cannot be undone from this page.
 */
export interface CancelSubscriptionResult {
  message: string;
  effective: 'period_end' | 'immediately';
  endsAt: string | null;
}

/**
 * Cancel subscription (at period end, or immediately for a granted plan)
 */
export const cancelSubscription = async (): Promise<CancelSubscriptionResult> => {
  return api.post('/billing/cancel', {});
};

/**
 * Reasons offered by the post-cancellation survey.
 *
 * Must stay in step with CANCELLATION_REASONS on the server, which rejects
 * anything outside this set. Labels are translated per id in the billing
 * locale files under `cancelFlow.reasons`.
 */
export const CANCELLATION_REASONS = [
  'too_expensive',
  'missing_features',
  'switching_service',
  'not_using',
  'too_complex',
  'temporary',
  'other',
] as const;

export type CancellationReason = (typeof CANCELLATION_REASONS)[number];

/** Server rejects anything longer, so the textarea stops here too */
export const CANCELLATION_COMMENT_MAX_LENGTH = 1000;

/**
 * Record why the customer cancelled.
 *
 * Answering is optional and the cancellation has already happened by this
 * point, so a failure here changes nothing about their subscription.
 */
export const submitCancellationFeedback = async (
  reason: CancellationReason,
  comment?: string
): Promise<{ message: string }> => {
  return api.post('/billing/cancellation-feedback', { reason, comment });
};

/**
 * Reactivate cancelled subscription
 */
export const reactivateSubscription = async (): Promise<{ message: string }> => {
  return api.post('/billing/reactivate', {});
};

/**
 * Get billing history (invoices, refunds, failed payments)
 * Returns a unified list of billing events sorted by date
 */
export const getInvoiceHistory = async (limit: number = 10): Promise<BillingHistoryEntry[]> => {
  return api.get(`/billing/invoices?limit=${limit}`);
};

/**
 * Check if organization can add a new member
 * Free plan = 1 seat only, paid plans = unlimited (billed per seat)
 */
export const canAddMember = async (): Promise<CanAddMemberResult> => {
  return api.get('/billing/can-add-member');
};

/**
 * Start a plan upgrade.
 *
 * Redirects to Stripe Checkout when the customer has no subscription yet. When
 * they already have one the change is completed server-side, and this resolves
 * with `changed: true` WITHOUT navigating — the caller is responsible for
 * telling the user it worked. Only report success on that flag: it is the one
 * signal that means the server actually applied and charged for the change.
 *
 * @param planId - The plan ID to upgrade to
 * @param billingCycle - 'monthly' or 'yearly'
 * @param region - Billing region ('JP' for Japan, 'DEFAULT' for others)
 */
export const redirectToCheckout = async (
  planId: number,
  billingCycle: 'monthly' | 'yearly',
  region: BillingRegion = 'DEFAULT'
): Promise<PlanUpgradeResult> => {
  const result = await createCheckoutSession(planId, billingCycle, region);

  if (result.changed) {
    return result;
  }

  if (result.checkoutUrl) {
    window.location.href = result.checkoutUrl;
    return result;
  }

  throw new Error('Failed to create checkout session');
};

/**
 * Redirect to Stripe Customer Portal
 */
export const redirectToPortal = async (): Promise<void> => {
  const { portalUrl } = await createPortalSession();
  if (portalUrl) {
    window.location.href = portalUrl;
  } else {
    throw new Error('Failed to create portal session');
  }
};

/**
 * Get current payment method
 */
export const getPaymentMethod = async (): Promise<PaymentMethod | null> => {
  const response = await api.get('/billing/payment-method') as { paymentMethod: PaymentMethod | null };
  return response.paymentMethod;
};

/**
 * Create SetupIntent for adding/updating payment method
 */
export const createSetupIntent = async (): Promise<SetupIntentResponse> => {
  return api.post('/billing/setup-intent', {});
};

/**
 * Update default payment method after successful SetupIntent
 */
export const updatePaymentMethod = async (paymentMethodId: string): Promise<{ message: string }> => {
  return api.post('/billing/payment-method', { paymentMethodId });
};

/**
 * What an upgrade will cost, for the confirmation screen. Nothing is charged
 * to produce this.
 */
export interface UpgradePreview {
  currentPlan: { id: number; name: string };
  newPlan: { id: number; name: string; monthlyPrice: number; yearlyPrice: number };
  billingCycle: 'monthly' | 'yearly';
  currency: string;
  seats: number;
  /** Prorated amount charged on confirming — can be small mid-cycle. */
  dueToday: number;
  /** Charged every cycle from the next billing date onward. */
  recurringTotal: number;
  taxIncluded: boolean;
  taxRate: number;
  nextBillingDate: string;
}

/**
 * Price an upgrade before confirming it
 */
export const getUpgradePreview = async (
  planId: number,
  billingCycle: 'monthly' | 'yearly'
): Promise<UpgradePreview> => {
  return api.get(`/billing/upgrade-preview?planId=${planId}&billingCycle=${billingCycle}`);
};

export interface DowngradePreview {
  effectiveDate: string;
  newPlan: {
    id: number;
    name: string;
    monthlyPrice: number;
    yearlyPrice: number;
    currency: string;
  };
  currentPlan: {
    id: number;
    name: string;
  };
}

/**
 * Get downgrade preview (pricing and effective date)
 */
export const getDowngradePreview = async (
  planId: number,
  billingCycle: 'monthly' | 'yearly',
  region: BillingRegion = 'DEFAULT'
): Promise<DowngradePreview> => {
  return api.get(`/billing/downgrade-preview?planId=${planId}&billingCycle=${billingCycle}&region=${region}`);
};

/**
 * Schedule a plan downgrade.
 *
 * Currency is NOT sent: the server derives it from the customer's country, the
 * same way it does for upgrades. What a downgrade will eventually bill is not
 * the browser's decision.
 */
export const scheduleDowngrade = async (
  planId: number,
  billingCycle: 'monthly' | 'yearly'
): Promise<{ effectiveDate: string; newPlanName: string }> => {
  return api.post('/billing/downgrade', { planId, billingCycle });
};

/**
 * Cancel a scheduled downgrade
 */
export const cancelDowngrade = async (): Promise<{ message: string }> => {
  return api.post('/billing/cancel-downgrade', {});
};

const billingService = {
  getBillingPageData,
  getStripeConfig,
  getCurrentBillingInfo,
  getAvailablePlans,
  createCheckoutSession,
  verifyCheckoutSession,
  createPortalSession,
  previewSeatChange,
  updateSeatCount,
  cancelSubscription,
  submitCancellationFeedback,
  reactivateSubscription,
  getInvoiceHistory,
  canAddMember,
  redirectToCheckout,
  redirectToPortal,
  getPaymentMethod,
  createSetupIntent,
  updatePaymentMethod,
  getCurrencyForRegion,
  getDefaultBillingRegion,
  getDowngradePreview,
  getUpgradePreview,
  scheduleDowngrade,
  cancelDowngrade,
};

export default billingService;
